import React, { FC } from 'react';
import { getDatePickerFormat } from 'utils/format';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Radio, RadioGroup, Typography, Box, CircularProgress, Button, Divider } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';

import InfoText from 'components/shared/info-text.component';
import { AutoScheduleDirection } from 'models/production-planning';
import { LoadingButton } from '@mui/lab';

type AutoSchedulingPanelProps = {
    direction: AutoScheduleDirection;
    onDirectionChange: (direction: AutoScheduleDirection) => void;
    startDate: Date | null;
    onStartDateChange: (date: Date | null) => void;
    internalDeliveryDate?: Date | null;
    loading?: boolean;
    onCalculate?: () => void;
    canCalculate?: boolean;
};

const AutoSchedulingPanel: FC<AutoSchedulingPanelProps> = ({
    direction,
    onDirectionChange,
    startDate,
    onStartDateChange,
    internalDeliveryDate,
    loading,
    onCalculate,
    canCalculate = true,
}) => {
    const { t: translate } = useTranslation(['orders']);

    const hasInternalDeliveryDate = !!internalDeliveryDate;

    return (
        <>
            <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h5">{translate('orders:AUTOMATIC_SCHEDULING')}</Typography>
                {loading && <CircularProgress size={16} />}
            </Box>
            <InfoText text={translate('orders:AUTOMATIC_SCHEDULING_INFO')} />

            <RadioGroup
                row
                value={direction}
                onChange={(_, value) => onDirectionChange(value as AutoScheduleDirection)}
            >
                <FormControlLabel
                    value={AutoScheduleDirection.FORWARD_FROM_NOW}
                    control={<Radio />}
                    label={translate('orders:SCHEDULE_FROM_DATE_FORWARD')}
                />
                <FormControlLabel
                    value={AutoScheduleDirection.BACKWARD_FROM_DELIVERY}
                    control={<Radio />}
                    label={translate('orders:SCHEDULE_FROM_DELIVERY_DATE_BACKWARD')}
                />
            </RadioGroup>

            {direction === AutoScheduleDirection.FORWARD_FROM_NOW && (
                <Box mb={1} maxWidth={250}>
                    <DateTimePicker
                        format={getDatePickerFormat('datetime')}
                        ampm={false}
                        views={['year', 'month', 'day', 'hours']}
                        value={startDate}
                        onChange={(date: Date | null) => onStartDateChange(date)}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                            },
                        }}
                    />
                </Box>
            )}

            {direction === AutoScheduleDirection.BACKWARD_FROM_DELIVERY && !hasInternalDeliveryDate && (
                <Box mb={1}>
                    <Typography variant="body2" color="warning.main">
                        {translate('orders:DEADLINE_REQUIRED_FOR_BACKWARD_SCHEDULING')}
                    </Typography>
                </Box>
            )}

            <Box>
                {onCalculate && (
                    <LoadingButton onClick={onCalculate} disabled={!canCalculate} loading={loading}>
                        {translate('orders:PERFORM_AUTOMATIC_SCHEDULING')}
                    </LoadingButton>
                )}
            </Box>
            <Box my={2}>
                <Divider />
            </Box>
        </>
    );
};

export default AutoSchedulingPanel;
